import React from "react";
import { Box, Card, Flex, Text } from "@mantine/core";
import { LineChart } from "@mantine/charts";
import { useTranslation } from "react-i18next";
import { OKNGainSegmentSeries, oknDirectionLabelKey } from "@utils/oknChartData";

const LEFT_EYE_COLOR = "#228be6";
const RIGHT_EYE_COLOR = "#fa5252";
const CHART_HEIGHT = 220;

interface OKNGainSeriesChartProps {
  series: OKNGainSegmentSeries;
}

const OKNGainSeriesChart: React.FC<OKNGainSeriesChartProps> = ({ series }) => {
  const { t } = useTranslation();

  const title = t("okn.gainSeriesPanelTitle", {
    direction: t(oknDirectionLabelKey(series.direction)),
    speed: series.speedDps,
  });

  const hasData = series.points.some((p) => p.left != null || p.right != null);

  return (
    <Card withBorder radius="md" p="sm">
      <Text fw={600} size="sm" ta="center" mb="xs">
        {title}
      </Text>
      <Box h={CHART_HEIGHT}>
        {hasData ? (
          <LineChart
            h={CHART_HEIGHT}
            data={series.points}
            dataKey="t"
            series={[
              { name: "left", label: t("okn.leftEye"), color: LEFT_EYE_COLOR },
              { name: "right", label: t("okn.rightEye"), color: RIGHT_EYE_COLOR },
            ]}
            curveType="linear"
            withDots
            connectNulls
            gridAxis="xy"
            withLegend
            legendProps={{ verticalAlign: "top", height: 28 }}
            xAxisLabel={t("okn.timeSeconds")}
            yAxisLabel={t("okn.gainSeriesYAxis")}
            xAxisProps={{ type: "number", domain: ["dataMin", "dataMax"] }}
            valueFormatter={(value) => (value != null ? Number(value).toFixed(2) : "—")}
            referenceLines={[{ y: 1, color: "gray.5" }]}
          />
        ) : (
          <Flex justify="center" align="center" h="100%">
            <Text c="dimmed" size="sm">
              {t("okn.noGainSeriesData")}
            </Text>
          </Flex>
        )}
      </Box>
    </Card>
  );
};

export default React.memo(OKNGainSeriesChart);
